import { useEffect, useRef, useState } from "react";
import { Play, X, Flag, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Compact, FIXED-SIZE thumbnail inside a Live comment. Fixed box = the row
// height is known before the media loads, so a reader scrolled back is never
// pushed around while pictures/videos arrive. Videos never autoplay here.
export function ChatMediaThumb({ message, onOpen }) {
  if (!message?.media_url) return null;
  const isVideo = message.media_type === "video";
  const isGif = message.media_type === "gif";
  return (
    <button
      type="button"
      onClick={() => onOpen(message)}
      data-testid={`live-chat-media-${message.id}`}
      aria-label={isVideo ? "Open video" : "Open picture"}
      data-media-type={message.media_type}
      className={`relative mt-1 block ${isGif ? "w-32 h-24" : "w-24 h-24"} overflow-hidden rounded-lg bg-black/40 border border-white/10 pointer-events-auto`}
    >
      {isVideo ? (
        <video src={message.media_url} preload="metadata" muted playsInline className="w-full h-full object-cover pointer-events-none" />
      ) : (
        <img src={message.media_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
      )}
      {isGif && <span className="absolute bottom-1 left-1 px-1 rounded bg-black/60 text-[8px] font-black tracking-wider text-white">GIF</span>}
      {isVideo && (
        <span className="absolute inset-0 grid place-items-center">
          <span className="h-8 w-8 rounded-full bg-black/60 grid place-items-center"><Play size={14} className="text-white ml-0.5" /></span>
        </span>
      )}
    </button>
  );
}

// Full-screen viewer: picture or video (controls, NO autoplay, sound only
// when the member presses play). Report for everyone; Remove for the author,
// host, stream moderator/admin or platform admin (server enforces).
export function ChatMediaViewer({ message, streamId, me, isHost, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  useEffect(() => () => { try { videoRef.current?.pause(); } catch { /* noop */ } }, []);
  if (!message) return null;
  const canRemove = !!me && (isHost || me.role === "admin" || me.id === message.user_id);

  const report = async () => {
    if (!me) { toast.error("Sign in to report."); return; }
    setBusy(true);
    try {
      await api.post("/reports", { target_type: "live_chat", target_id: message.id, reason: "Inappropriate live comment attachment" });
      toast.success("Reported — thank you.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not report."); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/live/${streamId}/chat/${message.id}`);
      onRemoved?.(message.id);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove."); } finally { setBusy(false); }
  };

  return (
    <div data-testid="live-chat-media-viewer" className="fixed inset-0 z-[90] bg-black/95 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-3 pt-[calc(env(safe-area-inset-top,0px)+8px)] pb-2 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-xs text-white/70">{message.display_name}{message.text ? ` · ${message.text}` : ""}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={report} disabled={busy} data-testid="live-chat-media-report" className="h-9 px-3 rounded-full bg-white/10 text-xs flex items-center gap-1"><Flag size={14} /> Report</button>
          {canRemove && (
            <button type="button" onClick={remove} disabled={busy} data-testid="live-chat-media-remove" className="h-9 px-3 rounded-full bg-rose-600/80 text-xs flex items-center gap-1"><Trash2 size={14} /> Remove</button>
          )}
          <button type="button" onClick={onClose} data-testid="live-chat-media-close" aria-label="Close" className="h-9 w-9 rounded-full bg-white/10 grid place-items-center"><X size={16} /></button>
        </div>
      </div>
      <div className="flex-1 min-h-0 grid place-items-center p-2" onClick={(e) => e.stopPropagation()}>
        {message.media_type === "video" ? (
          <video ref={videoRef} src={message.media_url} controls playsInline preload="metadata" className="max-h-full max-w-full rounded-lg" data-testid="live-chat-media-video" />
        ) : (
          <img src={message.media_url} alt="" className="max-h-full max-w-full object-contain rounded-lg" data-testid="live-chat-media-image" />
        )}
      </div>
    </div>
  );
}
